import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Divider, Form, Input, Select, Spin, Typography } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { useServerNotifications } from "../api/notifications";
import type {
  CircumscriptionSettings,
  CircumscriptionSettingsResponse,
  NotificationsResponse,
} from "../api/types";
import { CountrySelect } from "../components/CountrySelect";

type Config = CircumscriptionSettings["config"];

interface CircumscriptionSettingsValues {
  circumscription_code: string;
  denomination: string;
  country: string;
  language_id: number | null;
  contact_first_name: string;
  contact_last_name: string;
  phone: string;
  mobile: string;
  email: string;
  rewind_weeks: number | null;
  forward_weeks: number | null;
  min_speaker_re_req_months: number | null;
  apply_sp_re_req_min_assign_count: number | null;
  min_speaker_weeks_in_advance: number | null;
  max_speaker_weeks_in_advance: number | null;
  forward_sp_assign_weeks: number | null;
  min_weeks_whitout_sp_assign: number | null;
  max_weeks_whitout_sp_assign: number | null;
  min_weeks_whitout_sp_assign_sm: number | null;
  max_weeks_whitout_sp_assign_sm: number | null;
  hide_events_after_months: number | null;
}

/**
 * Which pair of config bounds backs which field. Several fields share a pair —
 * the two ends of a span are picked from the same range — so the mapping is
 * worth stating once instead of at every select.
 */
const BOUNDS: Record<string, [keyof Config, keyof Config]> = {
  rewind_weeks: ["min_rewind_weeks", "max_rewind_weeks"],
  forward_weeks: ["min_forward_weeks", "max_forward_weeks"],
  min_speaker_re_req_months: ["min_speaker_re_req_months", "max_speaker_re_req_months"],
  apply_sp_re_req_min_assign_count: [
    "min_apply_sp_re_req_min_assign_count",
    "max_apply_sp_re_req_min_assign_count",
  ],
  min_speaker_weeks_in_advance: ["min_speaker_weeks_in_advance", "max_speaker_weeks_in_advance"],
  max_speaker_weeks_in_advance: ["min_speaker_weeks_in_advance", "max_speaker_weeks_in_advance"],
  forward_sp_assign_weeks: ["min_forward_sp_assign_weeks", "max_forward_sp_assign_weeks"],
  min_weeks_whitout_sp_assign: [
    "min_weeks_between_speaker_assign",
    "max_weeks_between_speaker_assign",
  ],
  max_weeks_whitout_sp_assign: [
    "min_weeks_between_speaker_assign",
    "max_weeks_between_speaker_assign",
  ],
  min_weeks_whitout_sp_assign_sm: [
    "min_weeks_between_speaker_sm_assign",
    "max_weeks_between_speaker_sm_assign",
  ],
  max_weeks_whitout_sp_assign_sm: [
    "min_weeks_between_speaker_sm_assign",
    "max_weeks_between_speaker_sm_assign",
  ],
  hide_events_after_months: ["min_hide_events_after_months", "max_hide_events_after_months"],
};

/**
 * Sidebar entry "circumscription_settings" → /circumscription/circumscription_settings.
 *
 * The circumscription's own configuration: its name, its contact person, and
 * the scheduling parameters every congregation in it inherits. Needs full
 * access — the zone coordinator's *personal* settings are a separate screen an
 * observer may edit.
 *
 * Every bound comes from the site configuration, which is the web master's to
 * set. The same circumscription is also editable from /webmaster/circumscriptions,
 * but only there can it be created or deactivated.
 */
export function CircumscriptionSettingsPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<CircumscriptionSettingsValues>();

  const settings = useQuery({
    queryKey: ["circumscription", "settings"],
    queryFn: () => apiFetch<CircumscriptionSettingsResponse>("/circumscription/settings"),
  });

  useEffect(() => {
    if (!settings.data) return;

    const circ = settings.data.data.circumscription;
    form.setFieldsValue({
      circumscription_code: circ.circumscription_code ?? "",
      denomination: circ.denomination ?? "",
      country: circ.country ?? "",
      language_id: circ.language_id,
      contact_first_name: circ.contact_first_name ?? "",
      contact_last_name: circ.contact_last_name ?? "",
      phone: circ.phone ?? "",
      mobile: circ.mobile ?? "",
      email: circ.email ?? "",
      rewind_weeks: circ.rewind_weeks,
      forward_weeks: circ.forward_weeks,
      min_speaker_re_req_months: circ.min_speaker_re_req_months,
      apply_sp_re_req_min_assign_count: circ.apply_sp_re_req_min_assign_count,
      min_speaker_weeks_in_advance: circ.min_speaker_weeks_in_advance,
      max_speaker_weeks_in_advance: circ.max_speaker_weeks_in_advance,
      forward_sp_assign_weeks: circ.forward_sp_assign_weeks,
      min_weeks_whitout_sp_assign: circ.min_weeks_whitout_sp_assign,
      max_weeks_whitout_sp_assign: circ.max_weeks_whitout_sp_assign,
      min_weeks_whitout_sp_assign_sm: circ.min_weeks_whitout_sp_assign_sm,
      max_weeks_whitout_sp_assign_sm: circ.max_weeks_whitout_sp_assign_sm,
      hide_events_after_months: circ.hide_events_after_months,
    });
  }, [settings.data, form]);

  const update = useMutation({
    mutationFn: (values: CircumscriptionSettingsValues) =>
      apiFetch<NotificationsResponse>("/circumscription/settings", {
        method: "PUT",
        body: values,
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({ queryKey: ["circumscription", "settings"] });
      // The same record backs the web master's list and the contact screen.
      void queryClient.invalidateQueries({ queryKey: ["webmaster", "circumscriptions"] });
      void queryClient.invalidateQueries({ queryKey: ["circumscription", "contacts"] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  const config = settings.data?.data.config;

  const optionsFor = useMemo(() => {
    return (field: keyof typeof BOUNDS) => {
      if (!config) return [];

      const [minKey, maxKey] = BOUNDS[field];
      const options = [];
      for (let value = config[minKey]; value <= config[maxKey]; value += 1) {
        options.push({ value, label: String(value) });
      }
      return options;
    };
  }, [config]);

  if (settings.isPending) return <Spin />;

  if (settings.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={
          settings.error instanceof Error ? settings.error.message : String(settings.error)
        }
      />
    );
  }

  const languages = settings.data?.data.languages ?? [];
  const hint = (text: string) => <Typography.Text type="secondary">{text}</Typography.Text>;

  // The nine bounded selects, in the order the old view had them. Label and
  // hint are spelled out because scripts/check-i18n.mjs only sees literal keys.
  const parameters: { name: keyof typeof BOUNDS; label: string; hint: string }[] = [
    {
      name: "rewind_weeks",
      label: t("activerecord.attributes.circumscription.rewind_weeks"),
      hint: t("circumscription.hints.rewind_weeks"),
    },
    {
      name: "forward_weeks",
      label: t("activerecord.attributes.circumscription.forward_weeks"),
      hint: t("circumscription.hints.forward_weeks"),
    },
    {
      name: "min_speaker_re_req_months",
      label: t("activerecord.attributes.circumscription.min_speaker_re_req_months"),
      hint: t("circumscription.hints.min_speaker_re_req_months"),
    },
    {
      name: "apply_sp_re_req_min_assign_count",
      label: t("activerecord.attributes.circumscription.apply_sp_re_req_min_assign_count"),
      hint: t("circumscription.hints.apply_sp_re_req_min_assign_count"),
    },
    {
      name: "min_speaker_weeks_in_advance",
      label: t("activerecord.attributes.circumscription.min_speaker_weeks_in_advance"),
      hint: t("circumscription.hints.min_speaker_weeks_in_advance"),
    },
    {
      name: "max_speaker_weeks_in_advance",
      label: t("activerecord.attributes.circumscription.max_speaker_weeks_in_advance"),
      hint: t("circumscription.hints.max_speaker_weeks_in_advance"),
    },
    {
      name: "forward_sp_assign_weeks",
      label: t("activerecord.attributes.circumscription.forward_sp_assign_weeks"),
      hint: t("circumscription.hints.forward_sp_assign_weeks"),
    },
    {
      name: "min_weeks_whitout_sp_assign",
      label: t("activerecord.attributes.circumscription.min_weeks_whitout_sp_assign"),
      hint: t("circumscription.hints.min_weeks_whitout_sp_assign"),
    },
    {
      name: "max_weeks_whitout_sp_assign",
      label: t("activerecord.attributes.circumscription.max_weeks_whitout_sp_assign"),
      hint: t("circumscription.hints.max_weeks_whitout_sp_assign"),
    },
    {
      name: "min_weeks_whitout_sp_assign_sm",
      label: t("activerecord.attributes.circumscription.min_weeks_whitout_sp_assign_sm"),
      hint: t("circumscription.hints.min_weeks_whitout_sp_assign_sm"),
    },
    {
      name: "max_weeks_whitout_sp_assign_sm",
      label: t("activerecord.attributes.circumscription.max_weeks_whitout_sp_assign_sm"),
      hint: t("circumscription.hints.max_weeks_whitout_sp_assign_sm"),
    },
    {
      name: "hide_events_after_months",
      label: t("activerecord.attributes.circumscription.hide_events_after_months"),
      hint: t("circumscription.hints.hide_events_after_months"),
    },
  ];

  return (
    <Card title={t("navigation.circumscription_settings")} style={{ maxWidth: 720 }}>
      <Form<CircumscriptionSettingsValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => update.mutate(values)}
      >
        <Divider titlePlacement="start">
          {t("circumscription.headers.circumscription_data")}
        </Divider>

        <Form.Item
          name="denomination"
          label={t("activerecord.attributes.circumscription.denomination")}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="circumscription_code"
          label={t("activerecord.attributes.circumscription.circumscription_code")}
          rules={[{ required: true }]}
        >
          <Input style={{ width: 140 }} />
        </Form.Item>

        <Form.Item name="country" label={t("activerecord.attributes.circumscription.country")}>
          <CountrySelect style={{ width: 240 }} />
        </Form.Item>

        <Form.Item
          name="language_id"
          label={t("activerecord.attributes.circumscription.language_id")}
          extra={hint(t("circumscription.hints.language_id"))}
        >
          <Select options={languages} style={{ width: 280 }} />
        </Form.Item>

        <Divider titlePlacement="start">
          {t("circumscription.headers.contact_person")}
        </Divider>

        <Form.Item
          name="contact_first_name"
          label={t("activerecord.attributes.circumscription.contact_first_name")}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="contact_last_name"
          label={t("activerecord.attributes.circumscription.contact_last_name")}
        >
          <Input />
        </Form.Item>

        <Form.Item name="phone" label={t("activerecord.attributes.circumscription.phone")}>
          <Input />
        </Form.Item>

        <Form.Item name="mobile" label={t("activerecord.attributes.circumscription.mobile")}>
          <Input />
        </Form.Item>

        <Form.Item
          name="email"
          label={t("activerecord.attributes.circumscription.email")}
          rules={[{ type: "email" }]}
        >
          <Input />
        </Form.Item>

        <Divider titlePlacement="start">
          {t("circumscription.headers.settings")}
        </Divider>

        {parameters.map((parameter) => (
          <Form.Item
            key={parameter.name}
            name={parameter.name}
            label={parameter.label}
            extra={hint(parameter.hint)}
          >
            <Select options={optionsFor(parameter.name)} style={{ width: 120 }} />
          </Form.Item>
        ))}

        <Button type="primary" htmlType="submit" loading={update.isPending}>
          {t("form.update")}
        </Button>
      </Form>
    </Card>
  );
}
